/**
 * Small helpers around localStorage so we can persist game state
 * and UI preferences across reloads. Falls back gracefully if
 * storage is unavailable (private mode, SSR-like environments).
 */

import type { Mode } from "./types";

const BORDERS_KEY = "hadej-cesko:borders-visible:v1";
const MODE_KEY = "hadej-cesko:mode:v1";

export function loadGuesses(storageKey: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string");
  } catch {
    return [];
  }
}

export function saveGuesses(storageKey: string, ids: Iterable<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...ids]));
  } catch {
    /* ignore quota / privacy errors */
  }
}

/**
 * Per-mode "finished" flag. We derive the key from the guesses
 * storage key so each mode has its own — flipping mode shouldn't
 * leak the other mode's finished state.
 */
const finishedKey = (guessesKey: string) => `${guessesKey}:finished`;

export function loadFinished(guessesKey: string): boolean {
  try {
    return localStorage.getItem(finishedKey(guessesKey)) === "true";
  } catch {
    return false;
  }
}

export function saveFinished(guessesKey: string, value: boolean): void {
  try {
    if (value) {
      localStorage.setItem(finishedKey(guessesKey), "true");
    } else {
      // Don't bother storing "false" – the loader treats missing
      // as false and this keeps localStorage tidy after a reset.
      localStorage.removeItem(finishedKey(guessesKey));
    }
  } catch {
    /* ignore */
  }
}

export function loadBordersVisible(defaultValue: boolean): boolean {
  try {
    const raw = localStorage.getItem(BORDERS_KEY);
    if (raw === null) return defaultValue;
    return raw === "true";
  } catch {
    return defaultValue;
  }
}

export function saveBordersVisible(value: boolean): void {
  try {
    localStorage.setItem(BORDERS_KEY, String(value));
  } catch {
    /* ignore */
  }
}

const ALL_MODES: readonly Mode[] = [
  "obce",
  "okresy",
  "kraje",
  "chkonp",
  "praha",
  "brno",
  "metro",
  "reky",
  "hory",
  "hory-prominence",
];

const LEGACY_CHKO_NP_MODES = ["parky", "chko"] as const;
const LEGACY_CHKO_NP_GUESS_KEYS = [
  "hadej-cesko:guesses:parky:v1",
  "hadej-cesko:guesses:chko:v1",
] as const;

export function loadMode(defaultValue: Mode): Mode {
  try {
    const raw = localStorage.getItem(MODE_KEY);
    if (raw && (ALL_MODES as readonly string[]).includes(raw)) return raw as Mode;
    if (raw && (LEGACY_CHKO_NP_MODES as readonly string[]).includes(raw)) {
      return "chkonp";
    }
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

export function saveMode(value: Mode): void {
  try {
    localStorage.setItem(MODE_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Merge guesses from the legacy NP / CHKO modes into the combined mode. */
export function loadLegacyChkoNpGuesses(validIds: Set<string>): string[] {
  const out = new Set<string>();
  for (const key of LEGACY_CHKO_NP_GUESS_KEYS) {
    for (const id of loadGuesses(key)) {
      if (validIds.has(id)) out.add(id);
    }
  }
  return [...out];
}

export function loadLegacyChkoNpFinished(): boolean {
  return LEGACY_CHKO_NP_GUESS_KEYS.some((key) => loadFinished(key));
}

// ── Daily progress ────────────────────────────────────────────────────

const DAILY_KEY = "hadej-cesko:daily:v1";

/** Per-difficulty-tier flags for the "quiz" daily format. */
export interface DailyQuizProgress {
  solved: Partial<Record<"easy" | "medium" | "hard", boolean>>;
  hintsRevealed: Partial<Record<"easy" | "medium" | "hard", boolean>>;
}

/** The two independent hints of the "where-is" daily format. */
export interface DailyWhereIsProgress {
  distances: boolean;
  region: boolean;
}

/** One pin the player dropped in the "pinpoint" daily format. */
export interface DailyPinpointGuess {
  lat: number;
  lng: number;
}

export interface DailyPinpointTierProgress {
  solved: boolean;
  /** Every confirmed pin, oldest first – the last one is the winner when solved. */
  guesses: DailyPinpointGuess[];
}

export interface DailyPinpointProgress {
  easy: DailyPinpointTierProgress;
  harder: DailyPinpointTierProgress;
  /** Czechia outline hint – one toggle for the map, shared by both tiers. */
  outline: boolean;
}

export function emptyPinpointProgress(): DailyPinpointProgress {
  return {
    easy: { solved: false, guesses: [] },
    harder: { solved: false, guesses: [] },
    outline: false,
  };
}

/** Picks made in the "odd-one-out" daily format. */
export interface DailyOddOneOutProgress {
  /** Every option the player clicked, oldest first. */
  picks: string[];
}

export function emptyOddOneOutProgress(): DailyOddOneOutProgress {
  return { picks: [] };
}

/** Picks made in the "higher-lower" daily format. */
export interface DailyHigherLowerProgress {
  /** One option index per played round, oldest first – round `i` picked `picks[i]`. */
  picks: number[];
}

export function emptyHigherLowerProgress(): DailyHigherLowerProgress {
  return { picks: [] };
}

/** Arrangement made in the "organize" daily format. */
export interface DailyOrganizeProgress {
  /** Item names as the player left them, leftmost (or topmost) first. */
  order: string[];
  /** How many times the player had the arrangement checked. */
  attempts: number;
}

export function emptyOrganizeProgress(): DailyOrganizeProgress {
  return { order: [], attempts: 0 };
}

/** Stops named in the "trip" daily format. */
export interface DailyTripProgress {
  /** Sequence indices the player has already filled in correctly. */
  solved: number[];
}

export function emptyTripProgress(): DailyTripProgress {
  return { solved: [] };
}

/** Items binned in the "sort" daily format. */
export interface DailySortProgress {
  /** Item name → category index; items still in the pile are absent. */
  placement: Record<string, number>;
  /** How many times the player had the bins checked. */
  attempts: number;
}

export function emptySortProgress(): DailySortProgress {
  return { placement: {}, attempts: 0 };
}

export interface DailyDayProgress {
  solved: boolean;
  hintsRevealed: number;
  /** Player gave up instead of solving – answer is revealed but it counts as a loss. */
  givenUp: boolean;
  /** Only present/relevant for entries with `type: "quiz"`. */
  quiz?: DailyQuizProgress;
  /** Only present/relevant for entries with `type: "where-is"`. */
  whereIs?: DailyWhereIsProgress;
  /** Only present/relevant for entries with `type: "pinpoint"`. */
  pinpoint?: DailyPinpointProgress;
  /** Only present/relevant for entries with `type: "odd-one-out"`. */
  oddOneOut?: DailyOddOneOutProgress;
  /** Only present/relevant for entries with `type: "higher-lower"`. */
  higherLower?: DailyHigherLowerProgress;
  /** Only present/relevant for entries with `type: "organize"`. */
  organize?: DailyOrganizeProgress;
  /** Only present/relevant for entries with `type: "trip"`. */
  trip?: DailyTripProgress;
  /** Only present/relevant for entries with `type: "sort"`. */
  sort?: DailySortProgress;
}

export type DailyProgressStore = Record<string, DailyDayProgress>;

function parseQuizProgress(raw: unknown): DailyQuizProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const parseFlags = (input: unknown): Partial<Record<"easy" | "medium" | "hard", boolean>> => {
    if (!input || typeof input !== "object" || Array.isArray(input)) return {};
    const src = input as Record<string, unknown>;
    const out: Partial<Record<"easy" | "medium" | "hard", boolean>> = {};
    for (const tier of ["easy", "medium", "hard"] as const) {
      if (src[tier] === true) out[tier] = true;
    }
    return out;
  };
  return { solved: parseFlags(v.solved), hintsRevealed: parseFlags(v.hintsRevealed) };
}

function parseWhereIsProgress(raw: unknown): DailyWhereIsProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  return { distances: v.distances === true, region: v.region === true };
}

function parsePinpointProgress(raw: unknown): DailyPinpointProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const parseTier = (input: unknown): DailyPinpointTierProgress => {
    if (!input || typeof input !== "object" || Array.isArray(input)) {
      return { solved: false, guesses: [] };
    }
    const tier = input as Record<string, unknown>;
    const guesses = Array.isArray(tier.guesses)
      ? tier.guesses.flatMap((g): DailyPinpointGuess[] => {
          if (!g || typeof g !== "object") return [];
          const { lat, lng } = g as Record<string, unknown>;
          if (typeof lat !== "number" || typeof lng !== "number") return [];
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
          return [{ lat, lng }];
        })
      : [];
    return { solved: tier.solved === true, guesses };
  };
  return {
    easy: parseTier(v.easy),
    harder: parseTier(v.harder),
    outline: v.outline === true,
  };
}

function parseOddOneOutProgress(raw: unknown): DailyOddOneOutProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const picks = Array.isArray(v.picks)
    ? v.picks.filter((p): p is string => typeof p === "string")
    : [];
  return { picks };
}

function parseHigherLowerProgress(raw: unknown): DailyHigherLowerProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const picks = Array.isArray(v.picks)
    ? v.picks.filter((p): p is number => typeof p === "number" && Number.isInteger(p) && p >= 0)
    : [];
  return { picks };
}

function parseOrganizeProgress(raw: unknown): DailyOrganizeProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const order = Array.isArray(v.order)
    ? v.order.filter((n): n is string => typeof n === "string")
    : [];
  const attempts =
    typeof v.attempts === "number" && Number.isInteger(v.attempts) && v.attempts >= 0
      ? v.attempts
      : 0;
  return { order, attempts };
}

function parseTripProgress(raw: unknown): DailyTripProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const solved = Array.isArray(v.solved)
    ? v.solved.filter(
        (n): n is number => typeof n === "number" && Number.isInteger(n) && n >= 0,
      )
    : [];
  return { solved };
}

function parseSortProgress(raw: unknown): DailySortProgress | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const v = raw as Record<string, unknown>;
  const placement: Record<string, number> = {};
  if (v.placement && typeof v.placement === "object" && !Array.isArray(v.placement)) {
    for (const [name, index] of Object.entries(v.placement)) {
      if (typeof index === "number" && Number.isInteger(index) && index >= 0) {
        placement[name] = index;
      }
    }
  }
  const attempts =
    typeof v.attempts === "number" && Number.isInteger(v.attempts) && v.attempts >= 0
      ? v.attempts
      : 0;
  return { placement, attempts };
}

export function loadDailyProgress(): DailyProgressStore {
  try {
    const raw = localStorage.getItem(DAILY_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: DailyProgressStore = {};
    for (const [key, val] of Object.entries(parsed)) {
      if (!val || typeof val !== "object" || Array.isArray(val)) continue;
      const v = val as Record<string, unknown>;
      const quiz = parseQuizProgress(v.quiz);
      const whereIs = parseWhereIsProgress(v.whereIs);
      const pinpoint = parsePinpointProgress(v.pinpoint);
      const oddOneOut = parseOddOneOutProgress(v.oddOneOut);
      const higherLower = parseHigherLowerProgress(v.higherLower);
      const organize = parseOrganizeProgress(v.organize);
      const trip = parseTripProgress(v.trip);
      const sort = parseSortProgress(v.sort);
      out[key] = {
        solved: v.solved === true,
        hintsRevealed: typeof v.hintsRevealed === "number" ? v.hintsRevealed : 0,
        givenUp: v.givenUp === true,
        ...(quiz ? { quiz } : {}),
        ...(whereIs ? { whereIs } : {}),
        ...(pinpoint ? { pinpoint } : {}),
        ...(oddOneOut ? { oddOneOut } : {}),
        ...(higherLower ? { higherLower } : {}),
        ...(organize ? { organize } : {}),
        ...(trip ? { trip } : {}),
        ...(sort ? { sort } : {}),
      };
    }
    return out;
  } catch {
    return {};
  }
}

export function saveDailyProgress(store: DailyProgressStore): void {
  try {
    localStorage.setItem(DAILY_KEY, JSON.stringify(store));
  } catch {
    /* ignore */
  }
}

export function getDailyDayProgress(
  store: DailyProgressStore,
  dateKey: string,
): DailyDayProgress {
  return store[dateKey] ?? { solved: false, hintsRevealed: 0, givenUp: false };
}
